import { Link, useParams } from 'react-router-dom';
import { ContentScreen } from '../styles/ContentScreen';
import { courseLessonTypes } from './courseLessonTypes';
import { FiCheckCircle, FiCircle } from 'react-icons/fi';
import { Button } from '../components/Button';
import { useMemo } from 'react';
import { getUnitCirclePoint } from '../util/getUnitCirclePoint';
import { ShowSiteLoading } from '../SiteLoadingIndicator';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import {
   courseDocumentQueryOptions,
   courseProgressQueryOptions,
} from './queryGroups';
import { useSetLessonComplete } from './useSetLessonComplete';
import { useProcessedLessonProgress } from './useProcessedLessonProgress';

function CourseOverview() {
   const { courseId } = useParams();

   const [{ data: courseDocument }, { data: courseProgress }] = useQueries({
      queries: [
         courseDocumentQueryOptions(courseId),
         courseProgressQueryOptions(courseId),
      ],
   });

   if (courseDocument == null || courseProgress == null) {
      return <ShowSiteLoading />;
   }

   return (
      <CourseDisplay
         courseDocument={courseDocument}
         courseProgress={courseProgress}
      />
   );
}

function CourseDisplay({ courseDocument, courseProgress }) {
   const { completedLessonsSet, lastCompleteLesson, nextLesson } =
      useProcessedLessonProgress(courseDocument, courseProgress);

   // preload next lesson content????

   return (
      <ContentScreen>
         <h1>{courseDocument.title}</h1>
         {nextLesson && (
            <article style={{ marginBottom: '4em' }}>
               <h2 style={{ opacity: 0.75 }}>
                  {lastCompleteLesson == null ? 'Start' : 'Continue'} Learning
               </h2>
               <h3>{nextLesson.title}</h3>
               <p>{nextLesson.description}</p>
               <Button
                  className="button-cta"
                  as={Link}
                  to={`/app/course/${courseDocument.id}/${nextLesson.id}`}
               >
                  Start Lesson
               </Button>
            </article>
         )}
         <h2 style={{ opacity: 0.75 }}>Contents</h2>
         {courseDocument.sections.map(({ id, title, lessons }) => (
            <CourseSection
               key={id}
               id={id}
               title={title}
               lessons={lessons}
               completedLessonsSet={completedLessonsSet}
            />
         ))}
      </ContentScreen>
   );
}

function CourseSection({ title, lessons, completedLessonsSet }) {
   const { courseId } = useParams();

   const setLessonComplete = useSetLessonComplete(courseId);

   const sectionProgress = useMemo(
      () =>
         lessons.reduce((tally, { id: lessonId }) => {
            if (completedLessonsSet[lessonId]) {
               return tally + 1;
            }
            return tally;
         }, 0) / lessons.length,

      [completedLessonsSet, lessons]
   );

   return (
      <section>
         <h3>
            <SectionStatusIndicator progress={sectionProgress} />
            {title}
         </h3>
         <ul className="content-styled-list">
            {lessons.map(({ id: lessonId, title }) => (
               <LessonItem
                  key={lessonId}
                  id={lessonId}
                  title={title}
                  completed={completedLessonsSet[lessonId]}
                  onToggleComplete={() =>
                     // TODO: debounce
                     setLessonComplete({
                        lessonId,
                        newCompletedValue: !completedLessonsSet[lessonId],
                     })
                  }
               />
            ))}
         </ul>
      </section>
   );
}

function SectionStatusIndicator({ progress }) {
   const arcPath = useMemo(() => {
      const [unitX, unitY] = getUnitCirclePoint(360 * progress);

      return `M 12 2 A 10 10 0 ${progress > 0.5 ? 1 : 0} 1 ${unitX * 10 + 12} ${
         unitY * -10 + 12
      }`;
   }, [progress]);
   if (progress === 0) {
      return <FiCircle style={{ opacity: 0.33 }} />;
   }
   if (progress < 1) {
      return (
         <>
            <svg
               stroke="currentColor"
               fill="none"
               strokeWidth="2"
               viewBox="0 0 24 24"
               strokeLinecap="round"
               strokeLinejoin="round"
               height="1em"
               width="1em"
            >
               <circle cx="12" cy="12" r="10" style={{ opacity: 0.33 }} />
               <path d={arcPath} />
            </svg>
         </>
      );
   }
   return <FiCheckCircle />;
}

function LessonItem({ title, id, completed, onToggleComplete }) {
   const { courseId } = useParams();
   return (
      <li style={{ marginBottom: '0.33em' }}>
         <Button className="button-link" onClick={onToggleComplete}>
            {completed ? (
               <FiCheckCircle />
            ) : (
               // hover full opacity!!
               <FiCircle style={{ opacity: 0.33 }} />
            )}
         </Button>
         <Link to={`/app/course/${courseId}/${id}`} className="link">
            {title}
         </Link>
      </li>
   );
}

export { CourseOverview };
